import {
    useState,
} from 'react'

import {
    useLocation,
} from 'react-router-dom'

import {
    useQuery,
} from '@tanstack/react-query'

import { ShieldCheck } from 'lucide-react'

import preCheckClientViewMutation from './services/pre-check-client-view'
import Overlay from '../../Components/Overlay'

const FIELDS = [
    { label: "Full Name", key: "fullName" },
    { label: "Year of Birth", key: null },
    { label: "Last 4 SSN", key: null },
    { label: "Email", key: null },
    { label: "Phone", key: null },
]

const VerificationCard = ({ data }) => (
    <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl overflow-hidden">

        {/* Top verified badge */}
        <div className="flex flex-col items-center pt-8 pb-4">
            <div className="flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-2">
                <ShieldCheck className="w-9 h-9 text-green-600" strokeWidth={1.75} />
            </div>
            <p className="text-sm font-semibold text-green-700 tracking-wide uppercase">PreCheck Verified</p>
        </div>

        <hr className="border-gray-100 mx-6" />

        {/* Card body */}
        <div className="grid grid-cols-2">

            {/* Left — fields */}
            <div className="flex flex-col gap-5 p-8 border-r border-gray-100">
                <h2 className="text-base font-bold text-gray-700 mb-1">Verified ID Details</h2>
                {FIELDS.map(({ label, key: fieldKey }) => (
                    <div key={label}>
                        <p className="text-xs font-medium text-gray-500 mb-1">{label}</p>
                        {fieldKey && data?.[fieldKey] ? (
                            <p className="text-base font-semibold text-gray-800">{data[fieldKey]}</p>
                        ) : (
                            <div className="h-5 w-36 rounded bg-gray-900" title="This information is securely stored in our system." />
                        )}
                    </div>
                ))}
            </div>

            {/* Right — face image, exactly half card width, vertically centered */}
            <div className="flex flex-col items-center justify-center gap-2 p-8">
                <div className="w-full aspect-[3/4] rounded-xl overflow-hidden border-2 border-gray-200 bg-gray-100">
                    {data?.faceImageUrl ? (
                        <img
                            src={data.faceImageUrl}
                            alt="Verified face"
                            className="w-full h-full object-contain"
                        />
                    ) : (
                        <div className="w-full h-full bg-gray-200" />
                    )}
                </div>
                <p className="text-xs text-gray-400">ID Photo</p>
            </div>

        </div>

        {/* Footer note */}
        <div className="bg-gray-50 border-t border-gray-100 px-8 py-4">
            <p className="text-xs text-gray-500 text-center">
                The redacted fields are securely stored and verified in our system. They are intentionally not displayed on this screen.
            </p>
        </div>

    </div>
)

const PreCheckView = () => {
    const location = useLocation()

    const queryParams = new URLSearchParams(location.search)
    const key = queryParams.get('key')

    const [activeTab, setActiveTab] = useState('verification')

    const {
        data,
        error,
        isFetching,
    } = useQuery({
        queryKey: ['pre-check-client-view', key],
        queryFn: () => preCheckClientViewMutation({ key }),
        refetchOnWindowFocus: false,
    })

    if (error) {
        return (
            <div className="min-h-screen bg-gray-100 flex items-center justify-center">
                <div className="bg-white rounded-xl border border-red-200 p-8 text-center text-red-600 shadow">
                    Failed to load verification details.
                </div>
            </div>
        )
    }

    const hasResume = !!data?.resumeKey

    if (!hasResume) {
        return (
            <div className="min-h-screen bg-gray-100 flex items-center justify-center p-6">
                <VerificationCard data={data} />
                {isFetching && <Overlay />}
            </div>
        )
    }

    return (
        <div className="min-h-screen bg-gray-100 p-4 sm:p-6 flex flex-col items-center justify-start lg:justify-center">

            {/* Mobile tab switcher — hidden on desktop */}
            <div className="lg:hidden w-full max-w-2xl mb-4 flex rounded-xl border border-gray-200 bg-white overflow-hidden shadow-sm">
                <button
                    onClick={() => setActiveTab('verification')}
                    className={`flex-1 py-2.5 text-sm font-medium transition-colors ${activeTab === 'verification' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-50'}`}
                >
                    Verification
                </button>
                <button
                    onClick={() => setActiveTab('resume')}
                    className={`flex-1 py-2.5 text-sm font-medium transition-colors ${activeTab === 'resume' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-50'}`}
                >
                    Resume
                </button>
            </div>

            {/* Main two-column layout (desktop) */}
            <div className="w-full max-w-7xl flex flex-col lg:flex-row lg:items-stretch gap-6">

                {/* Verification card — hidden on mobile when resume tab active */}
                <div className={`flex justify-center lg:block lg:w-1/3 ${activeTab === 'resume' ? 'hidden' : 'block'}`}>
                    <VerificationCard data={data} />
                </div>

                {/* Resume iFrame — hidden on mobile when verification tab active */}
                <div className={`lg:block lg:w-2/3 ${activeTab === 'verification' ? 'hidden' : 'block'}`}>
                    <div className="bg-white rounded-2xl shadow-xl overflow-hidden w-full min-h-[80svh] lg:min-h-0 lg:h-full">
                        <iframe
                            src={data.resumeKey}
                            title="Resume"
                            className="w-full min-h-[80svh] lg:min-h-0 lg:h-full border-0"
                        />
                    </div>
                </div>

            </div>

            {isFetching && <Overlay />}
        </div>
    )
}

export default PreCheckView
